import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Stack, LinearProgress, Button, Skeleton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  Inventory2, CheckCircleOutline, Lock, PauseCircleOutlined, LocalShipping,
  WarningAmber, ErrorOutline, EventBusy, TrendingDown, Block, Refresh, ChevronRight,
  ArrowDownward, ArrowUpward, SwapVert,
} from '@mui/icons-material';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Cell, Legend,
} from 'recharts';
import inventoryCommandService from '../../services/inventoryCommandService';
import { formatIndianNumber } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../dashboard/chartTokens';
import {
  StatCard, TableSurface, headerCellSx, bodyRowSx, StatusDot, StatusTone, ErrorState, TableEmptyState,
} from '../../components/common/pageKit';

// ---------------------------------------------------------------------------
// INVENTORY COMMAND CENTER — richer replacement for InventoryControlTower.
// "How much, where, available, reserved, blocked, at risk?" Full-bleed
// control-tower idiom (matches OrderFulfillmentControl / ProcurementCommandCenter)
// with pageKit StatCards for KPI tiles so money never clips. Data comes
// entirely from GET /inventory-command/command-center — real, tenant-scoped.
// ---------------------------------------------------------------------------

const TYPE_LABEL: Record<string, string> = {
  RAW: 'Raw Material', SEMI: 'Work in Progress', FINISHED: 'Finished Goods',
  CONSUMABLE: 'Consumables', OTHER: 'Other',
};

/** Defensive field lookup — the backend contract for nested arrays (warehouse
 * snapshot, attention, top items, trend) hasn't pinned exact key names, so
 * every accessor tries a short list of plausible aliases before giving up. */
const pick = (row: any, keys: string[]): any => {
  if (!row) return undefined;
  for (const k of keys) if (row[k] !== undefined && row[k] !== null) return row[k];
  return undefined;
};

const fmtInt = (v: any): string => (v === null || v === undefined || v === '' ? '—' : Number(v).toLocaleString('en-IN'));

const severityTone = (s: any): StatusTone => {
  const v = String(s || '').toLowerCase();
  if (v.includes('crit') || v.includes('high') || v.includes('err')) return 'error';
  if (v.includes('warn') || v.includes('med')) return 'warning';
  if (v.includes('good') || v.includes('ok') || v.includes('low')) return 'success';
  return 'info';
};

/** count-or-{count,value} normaliser for the loosely-specified slowMoving /
 * nonMoving fields — accepts a bare number or an object shaped like
 * expiringSoon and always returns something StatCard can render. */
const asCount = (v: any): { n: number | null; sub: string | null } => {
  if (v === null || v === undefined) return { n: null, sub: null };
  if (typeof v === 'object') {
    const n = v.count ?? v.items ?? null;
    const sub = v.value != null ? formatIndianNumber(Number(v.value)) : null;
    return { n, sub };
  }
  return { n: Number(v), sub: null };
};

/** Detects an x-axis label field (first string key) and treats every other
 * numeric key as its own bar series — lets the movement trend render
 * correctly regardless of exactly which field names the API settles on. */
const seriesFromRows = (rows: any[]): { labelKey: string; seriesKeys: string[] } => {
  const first = rows[0] || {};
  const keys = Object.keys(first);
  const labelKey = keys.find((k) => typeof first[k] === 'string') || keys[0] || 'label';
  const seriesKeys = keys.filter((k) => k !== labelKey && typeof first[k] === 'number');
  return { labelKey, seriesKeys };
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, mb: 1, color: 'text.secondary' }}>
    {children}
  </Typography>
);

const InventoryCommandCenter: React.FC = () => {
  const navigate = useNavigate();
  const C = useControlRoomColors();
  const ct = useChartTokens();

  const [d, setD] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    inventoryCommandService.commandCenter()
      .then(setD)
      .catch((e: any) => setError(e?.message || 'Failed to load the inventory command center.'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const health = d?.health || {};
  const byType: any[] = d?.byType || [];
  const movements: any[] = d?.movements || [];
  const topItems: any[] = d?.topItems || [];
  const warehouseSnapshot: any[] = d?.warehouseSnapshot || [];
  const attention: any[] = d?.attention || [];
  const movementTrend: any[] = d?.movementTrend || [];

  const expiring = asCount(d?.expiringSoon);
  const slowMoving = asCount(d?.slowMoving);
  const nonMoving = asCount(d?.nonMoving);

  const healthCards = [
    { k: 'Healthy', v: health.healthy, c: C.good },
    { k: 'Low Stock', v: health.low, c: C.warn },
    { k: 'Critical', v: health.critical, c: C.bad },
    { k: 'Excess', v: health.excess, c: C.accent },
  ];

  const trend = seriesFromRows(movementTrend);

  const kpis = [
    { title: 'Stock Value', value: d?.totalValue != null ? formatIndianNumber(d.totalValue) : null, icon: <Inventory2 />, color: '#2563eb' },
    { title: 'Available', value: d?.availableValue != null ? formatIndianNumber(d.availableValue) : null, icon: <CheckCircleOutline />, color: '#16a34a' },
    { title: 'Reserved', value: d?.reservedValue != null ? formatIndianNumber(d.reservedValue) : null, icon: <Lock />, color: '#d97706' },
    { title: 'QC Hold', value: d?.qcHoldValue != null ? formatIndianNumber(d.qcHoldValue) : null, icon: <PauseCircleOutlined />, color: '#7c3aed' },
    { title: 'Incoming', value: d?.incomingValue != null ? formatIndianNumber(d.incomingValue) : null, icon: <LocalShipping />, color: '#0891b2' },
    { title: 'Low Stock', value: d?.lowStockCount != null ? fmtInt(d.lowStockCount) : null, icon: <WarningAmber />, color: '#d97706' },
    { title: 'Out of Stock', value: d?.outOfStockCount != null ? fmtInt(d.outOfStockCount) : null, icon: <ErrorOutline />, color: '#dc2626' },
    { title: 'Expiring Soon', value: expiring.n != null ? fmtInt(expiring.n) : null, icon: <EventBusy />, color: '#dc2626', footer: expiring.sub ? (
        <Typography variant="caption" color="text.secondary">{expiring.sub} at risk</Typography>
      ) : undefined },
    { title: 'Slow Moving', value: slowMoving.n != null ? fmtInt(slowMoving.n) : null, icon: <TrendingDown />, color: '#d97706' },
    { title: 'Non Moving', value: nonMoving.n != null ? fmtInt(nonMoving.n) : null, icon: <Block />, color: '#64748b' },
  ];

  return (
    <Box sx={{ bgcolor: C.bg, minHeight: '100%', m: -3, color: C.text }}>
      {/* Header */}
      <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${C.line}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, background: `linear-gradient(90deg, ${C.panel}, ${C.bg})` }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Inventory2 sx={{ color: C.cyan }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 20 }}>Inventory Command Center</Typography>
            <Typography sx={{ color: C.sub, fontSize: 13 }}>How much, where, available, reserved, blocked, at risk?</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={load} sx={{ color: C.text, borderColor: C.line }}>Refresh</Button>
          <Button size="small" variant="outlined" onClick={() => navigate('/inventory/stock')} sx={{ color: C.text, borderColor: C.line }}>Stock List</Button>
        </Stack>
      </Box>

      {loading && <LinearProgress />}

      {error && !d ? (
        <ErrorState message={error} onRetry={load} />
      ) : (
        <>
          {/* KPI band */}
          <Box sx={{ px: 3, py: 2.5, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1.5 }}>
            {loading && !d
              ? Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} variant="rounded" height={92} sx={{ borderRadius: 3 }} />)
              : kpis.map((k) => <StatCard key={k.title} title={k.title} value={k.value} icon={k.icon} color={k.color} footer={k.footer} />)}
          </Box>

          {/* Value by type + Health/Movement */}
          <Box sx={{ px: 3, pb: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.3fr 1fr' }, gap: 2 }}>
            <Box>
              <SectionLabel>Stock Value by Type · click to view stock list</SectionLabel>
              <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 2, height: 300 }}>
                {byType.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={byType.map((t) => ({ ...t, label: TYPE_LABEL[t.type] || t.type }))} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid {...gridProps(ct)} />
                      <XAxis dataKey="label" {...axisProps(ct)} />
                      <YAxis {...axisProps(ct)} width={64} tickFormatter={(v: number) => formatIndianNumber(v)} />
                      <RTooltip {...tooltipStyle(ct)} formatter={(v: any, n: any, p: any) => [formatIndianNumber(Number(v)), `${p?.payload?.items ?? '—'} items`]} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]} cursor="pointer" onClick={() => navigate('/inventory/stock')}>
                        {byType.map((_, i) => <Cell key={i} fill={ct.series(i)} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <TableEmptyState title="No stock value by type" hint="No stock has been valued yet." />
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <SectionLabel>Stock Health · {health.total ?? 0} SKUs</SectionLabel>
                <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 2 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 1.25, mb: 1.5 }}>
                    {healthCards.map((x) => (
                      <Box key={x.k} sx={{ bgcolor: C.panel2, border: `1px solid ${C.line}`, borderRadius: 1.5, p: 1.25, textAlign: 'center' }}>
                        <Typography sx={{ fontWeight: 800, fontSize: 20, color: x.c, fontFamily: 'monospace' }}>{x.v ?? 0}</Typography>
                        <Typography sx={{ fontSize: 10.5, color: C.sub }}>{x.k}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden' }}>
                    {healthCards.map((x) => (health.total ? <Box key={x.k} sx={{ width: `${((x.v || 0) / health.total) * 100}%`, bgcolor: x.c }} /> : null))}
                  </Box>
                </Box>
              </Box>

              <Box>
                <SectionLabel>Today's Movement</SectionLabel>
                <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 2 }}>
                  <Stack spacing={1}>
                    {movements.map((mv: any, i: number) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, bgcolor: C.panel2, borderRadius: 1 }}>
                        {mv.type === 'IN' ? <ArrowDownward sx={{ color: C.good, fontSize: 18 }} /> : mv.type === 'OUT' ? <ArrowUpward sx={{ color: C.warn, fontSize: 18 }} /> : <SwapVert sx={{ color: C.sub, fontSize: 18 }} />}
                        <Typography sx={{ flex: 1, fontSize: 12.5 }}>{mv.type === 'IN' ? 'Received' : mv.type === 'OUT' ? 'Issued / Dispatched' : mv.type}</Typography>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: 12.5, color: mv.type === 'IN' ? C.good : mv.type === 'OUT' ? C.warn : C.text }}>{mv.type === 'IN' ? '+' : mv.type === 'OUT' ? '-' : ''}{fmtInt(mv.qty)}</Typography>
                        <Typography sx={{ fontSize: 10.5, color: C.sub }}>{fmtInt(mv.count)} moves</Typography>
                      </Box>
                    ))}
                    {!loading && !movements.length && <Typography sx={{ color: C.sub, fontSize: 13, py: 1, textAlign: 'center' }}>No stock movement today.</Typography>}
                  </Stack>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Warehouse snapshot */}
          <Box sx={{ px: 3, pb: 2 }}>
            <SectionLabel>Warehouse Snapshot · click a row to open Warehouse 360</SectionLabel>
            <TableSurface>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Warehouse', 'Stock Value', 'Available', 'Reserved', 'QC Hold', 'Pending Receipts'].map((h, i) => (
                        <TableCell key={h} sx={headerCellSx} align={i === 0 ? 'left' : 'right'}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {warehouseSnapshot.map((w: any, i: number) => {
                      const id = pick(w, ['warehouseId', 'id']);
                      const name = pick(w, ['warehouse', 'warehouseName', 'name']);
                      const code = pick(w, ['warehouseCode', 'code']);
                      return (
                        <TableRow
                          key={id ?? i}
                          hover
                          sx={{ ...(bodyRowSx as any), cursor: id ? 'pointer' : 'default' }}
                          onClick={() => id && navigate(`/inventory/warehouse/${id}`)}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{name || '—'}</Typography>
                            {code && <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{code}</Typography>}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatIndianNumber(Number(pick(w, ['value', 'stockValue']) ?? 0))}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatIndianNumber(Number(pick(w, ['available', 'availableValue']) ?? 0))}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatIndianNumber(Number(pick(w, ['reserved', 'reservedValue']) ?? 0))}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatIndianNumber(Number(pick(w, ['qcHold', 'qcHoldValue']) ?? 0))}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(pick(w, ['pendingReceipts', 'pending', 'incoming']))}</TableCell>
                        </TableRow>
                      );
                    })}
                    {!warehouseSnapshot.length && (
                      <TableRow><TableCell colSpan={6} sx={{ border: 0 }}><TableEmptyState title="No warehouse data" hint="No warehouses have stock yet." /></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </TableSurface>
          </Box>

          {/* Attention + Trend */}
          <Box sx={{ px: 3, pb: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.3fr' }, gap: 2 }}>
            <Box>
              <SectionLabel>Attention Required</SectionLabel>
              <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 1.5 }}>
                <Stack spacing={1}>
                  {attention.map((a: any, i: number) => {
                    const label = pick(a, ['label', 'message', 'title']);
                    const link = pick(a, ['link', 'route', 'href']);
                    return (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 1, borderRadius: 1, bgcolor: C.panel2, border: `1px solid ${C.line}` }}>
                        <StatusDot label="" tone={severityTone(a.severity)} />
                        <Typography sx={{ flex: 1, fontSize: 12.5, minWidth: 0 }} noWrap>{label || '—'}</Typography>
                        {link && (
                          <Button size="small" endIcon={<ChevronRight />} onClick={() => navigate(link)} sx={{ color: C.accent, textTransform: 'none', flexShrink: 0 }}>Open</Button>
                        )}
                      </Box>
                    );
                  })}
                  {!loading && !attention.length && <Typography sx={{ color: C.good, fontSize: 13, py: 2, textAlign: 'center' }}>All clear — nothing needs attention.</Typography>}
                </Stack>
              </Box>
            </Box>

            <Box>
              <SectionLabel>Movement Trend</SectionLabel>
              <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 2, height: 280 }}>
                {movementTrend.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={movementTrend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid {...gridProps(ct)} />
                      <XAxis dataKey={trend.labelKey} {...axisProps(ct)} />
                      <YAxis {...axisProps(ct)} width={54} />
                      <RTooltip {...tooltipStyle(ct)} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      {trend.seriesKeys.map((k, i) => (
                        <Bar key={k} dataKey={k} name={k} fill={ct.series(i)} radius={[3, 3, 0, 0]} maxBarSize={28} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <TableEmptyState title="No movement trend data" hint="No stock movements have been recorded recently." />
                )}
              </Box>
            </Box>
          </Box>

          {/* Top items */}
          <Box sx={{ px: 3, pb: 3 }}>
            <SectionLabel>Top Items by Value · click a row to open Item 360</SectionLabel>
            <TableSurface>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerCellSx}>Code</TableCell>
                      <TableCell sx={headerCellSx}>Product</TableCell>
                      <TableCell sx={headerCellSx} align="right">Stock</TableCell>
                      <TableCell sx={headerCellSx} align="right">Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topItems.map((it: any, i: number) => {
                      const productId = pick(it, ['productId', 'id']);
                      return (
                        <TableRow
                          key={productId ?? i}
                          hover
                          sx={{ ...(bodyRowSx as any), cursor: productId ? 'pointer' : 'default' }}
                          onClick={() => productId && navigate(`/inventory/item/${productId}`)}
                        >
                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{pick(it, ['code', 'productCode']) || '—'}</TableCell>
                          <TableCell>{pick(it, ['product', 'name', 'productName']) || '—'}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(pick(it, ['stock', 'qty', 'onHand']))}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatIndianNumber(Number(pick(it, ['value', 'stockValue']) ?? 0))}</TableCell>
                        </TableRow>
                      );
                    })}
                    {!topItems.length && (
                      <TableRow><TableCell colSpan={4} sx={{ border: 0 }}><TableEmptyState title="No stock items" hint="No stock has been valued yet." /></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </TableSurface>
          </Box>
        </>
      )}
    </Box>
  );
};

export default InventoryCommandCenter;
